/**
 * Notification inbox state for the mobile app (#4282) — one instance shared by
 * the top-bar bell and the /m/notifications list, so the unread dot and the
 * list never disagree. Provided by MobileLayout.
 *
 * Freshness: SSE `notification` events (live), a refetch whenever the app
 * comes back to the foreground, and a 5-minute poll as the fallback — the
 * same cadence the web SidebarLayout uses.
 *
 * Contract: docs/features/mobile-notifications-tray.md
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { notifications as notificationsApi, type NotificationItem } from '@/lib/api'
import { useSSEEvent } from '@/hooks/useSSE'

const POLL_MS = 5 * 60 * 1000

type Status = 'loading' | 'ready' | 'error'

export interface NotificationInbox {
  items: NotificationItem[]
  unreadCount: number
  status: Status
  refresh: () => Promise<void>
  markRead: (item: NotificationItem) => void
  markAllRead: () => void
}

const InboxContext = createContext<NotificationInbox | null>(null)

export function NotificationInboxProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [status, setStatus] = useState<Status>('loading')

  const refresh = useCallback(async () => {
    try {
      const data = await notificationsApi.list()
      setItems(data.items || [])
      setUnreadCount(data.unread_count || 0)
      setStatus('ready')
    } catch {
      // Keep whatever we already showed; only surface an error if we have nothing.
      setStatus(prev => (prev === 'ready' ? 'ready' : 'error'))
    }
  }, [])

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, POLL_MS)
    const onVisible = () => { if (document.visibilityState === 'visible') refresh() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [refresh])

  // Live: the SSE payload is partial (no link_url / event_type), so bump the
  // badge immediately and refetch for the full row.
  useSSEEvent('notification', useCallback(() => {
    setUnreadCount(prev => prev + 1)
    refresh()
  }, [refresh]))

  const markRead = useCallback((item: NotificationItem) => {
    if (item.read_at) return
    const readAt = new Date().toISOString()
    setItems(prev => prev.map(n => (n.id === item.id ? { ...n, read_at: readAt } : n)))
    setUnreadCount(prev => Math.max(0, prev - 1))
    notificationsApi.markRead(Number(item.id)).catch(() => {
      setItems(prev => prev.map(n => (n.id === item.id ? { ...n, read_at: null } : n)))
      setUnreadCount(prev => prev + 1)
    })
  }, [])

  const markAllRead = useCallback(() => {
    const readAt = new Date().toISOString()
    let previous: NotificationItem[] = []
    let previousCount = 0
    setItems(prev => { previous = prev; return prev.map(n => (n.read_at ? n : { ...n, read_at: readAt })) })
    setUnreadCount(prev => { previousCount = prev; return 0 })
    notificationsApi.markAllRead().catch(() => {
      setItems(previous)
      setUnreadCount(previousCount)
    })
  }, [])

  const value = useMemo<NotificationInbox>(
    () => ({ items, unreadCount, status, refresh, markRead, markAllRead }),
    [items, unreadCount, status, refresh, markRead, markAllRead],
  )
  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>
}

export function useNotificationInbox(): NotificationInbox {
  const ctx = useContext(InboxContext)
  if (!ctx) throw new Error('useNotificationInbox must be used inside NotificationInboxProvider (MobileLayout)')
  return ctx
}
